import { useCallback, useEffect, useRef, useState, type FC } from 'react';

import { observeElementResize } from '#/lib/observe-element-resize';
import type { SanityFeatureComparison } from '#/lib/sanity/queries/featureComparison';
import {
  getSectionButtonTheme,
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
} from '#/lib/section-theme';
import { cn } from '#/lib/utils';
import { ArrowRight } from '#/components/ArrowRight';
import { BasicText } from '#/components/BasicText';
import { ButtonRow } from '#/components/ButtonRow';
import { RichText } from '#/components/RichText';
import { Tooltip } from '#/components/Tooltip';

import { CellIcon } from './CellIcon';

const SCROLL_FADE_CLASSES: Record<SanityFeatureComparison['theme'], string> = {
  light: 'from-grayscale-01',
  charcoal: 'from-charcoal',
};

export const FeatureComparison: FC<SanityFeatureComparison> = (props) => {
  const isDark = isDarkSectionTheme(props.theme);
  const accentClass = getSectionThemeAccent(props.theme);
  const surfaceClass = getSectionThemeSurface(props.theme);
  const buttonTheme = getSectionButtonTheme(props.theme);

  const borderClass = isDark ? 'border-grayscale-05' : 'border-grayscale-03';
  const headerTextClass = isDark ? 'text-cloud' : 'text-charcoal';
  const cellLabelClass = isDark ? 'text-cloud' : 'text-charcoal';
  const dataCellWidthClasses = 'max-m:min-w-180';

  const scrollerRef = useRef<HTMLDivElement>(null);
  const [showScrollFade, setShowScrollFade] = useState(false);
  const [showStickyShadow, setShowStickyShadow] = useState(false);
  // Hint arrow: only before the user starts scrolling.
  const [showScrollHint, setShowScrollHint] = useState(false);
  const hasStartedScrolling = useRef(false);

  const updateScrollState = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const canScroll = maxScroll > 1;
    const hasScrolled = scroller.scrollLeft > 1;

    if (hasScrolled) {
      hasStartedScrolling.current = true;
    }

    setShowStickyShadow(hasScrolled);
    setShowScrollFade(canScroll && scroller.scrollLeft < maxScroll - 1);
    setShowScrollHint(canScroll && !hasStartedScrolling.current);
  }, []);

  const scrollTableRight = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({ left: window.innerWidth * 0.5, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    updateScrollState();
    scroller.addEventListener('scroll', updateScrollState, { passive: true });
    const disconnect = observeElementResize(scroller, updateScrollState);

    return () => {
      scroller.removeEventListener('scroll', updateScrollState);
      disconnect();
    };
  }, [updateScrollState]);

  const rowHeaderClasses = cn(
    'sticky left-0 z-1 w-142 min-w-142 border-b p-10 text-left align-middle m:w-180 m:min-w-180',
    'transition-shadow duration-200',
    borderClass,
    surfaceClass,
    showStickyShadow &&
      (isDark
        ? 'shadow-[6px_0_16px_rgba(0,0,0,0.45)]'
        : 'shadow-[6px_0_16px_rgba(0,0,0,0.1)]'),
  );

  const hasIntro =
    props.eyebrow || props.title || props.description || props.buttons?.length;
  const hasFooterButtons = Boolean(props.footerButtons?.length);

  return (
    <div className={cn('p-main py-40 m:py-60 l:py-100', surfaceClass)}>
      <div className="mx-auto flex max-w-1235 flex-col gap-40">
        {hasIntro ? (
          <div className="flex max-w-800 flex-col gap-24 m:gap-48">
            {props.eyebrow ? (
              <p className={cn('type-eyebrow', accentClass)}>{props.eyebrow}</p>
            ) : null}

            {props.title || props.description ? (
              <div className="flex flex-col gap-20">
                {props.title ? (
                  <h2
                    className={cn(
                      'type-heading5 m:type-heading3',
                      isDark ? 'text-cloud' : 'text-charcoal',
                    )}
                  >
                    {props.title}
                  </h2>
                ) : null}
                {props.description ? (
                  <BasicText
                    value={props.description}
                    className={cn(
                      'type-paragraph',
                      isDark ? 'text-cloud' : 'text-charcoal',
                    )}
                  />
                ) : null}
              </div>
            ) : null}

            {props.buttons?.length ? (
              <div className="@container w-full">
                <ButtonRow buttons={props.buttons} theme={buttonTheme} />
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="relative">
          <div ref={scrollerRef} className="no-scrollbar overflow-x-auto">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className={rowHeaderClasses} />
                  {props.headers.map((header, index) => (
                    <th
                      key={index}
                      className={cn(
                        'border-b p-8 py-10 text-left align-top font-medium',
                        borderClass,
                        dataCellWidthClasses,
                      )}
                    >
                      <span
                        className={cn(
                          'text-14 leading-150 m:text-16 l:text-18',
                          headerTextClass,
                        )}
                      >
                        {header}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {props.rows.map((row) => (
                  <tr key={row._key}>
                    <th scope="row" className={rowHeaderClasses}>
                      <div
                        className={cn(
                          'flex items-center gap-10 text-14 font-medium leading-150 m:text-16',
                          headerTextClass,
                          isDark && '[&_svg]:!text-cloud',
                        )}
                      >
                        {row.featureLabel}
                        {row.featureContent?.length ? (
                          <Tooltip position="right">
                            <RichText
                              value={row.featureContent}
                              normalTextSizeClassName="text-12 font-[400] leading-120"
                            />
                          </Tooltip>
                        ) : null}
                      </div>
                    </th>
                    {row.cells.map((cell) => (
                      <td
                        key={cell._key}
                        className={cn(
                          'border-b px-10 py-8 align-middle',
                          borderClass,
                          dataCellWidthClasses,
                        )}
                      >
                        {!cell.isEmpty ? (
                          <div className="flex flex-col items-start gap-4">
                            <CellIcon icon={cell.icon} isDark={isDark} />
                            {cell.label ? (
                              <span
                                className={cn(
                                  'text-14 font-light leading-150',
                                  cellLabelClass,
                                )}
                              >
                                {cell.label}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-y-0 right-0 w-80 bg-gradient-to-l to-transparent transition-opacity duration-300',
              SCROLL_FADE_CLASSES[props.theme],
              showScrollFade ? 'opacity-100' : 'opacity-0',
            )}
          />

          <button
            type="button"
            onClick={scrollTableRight}
            aria-label="Scroll table to the right"
            className={cn(
              'absolute right-8 top-1/2 z-2 flex size-28 -translate-y-1/2 items-center justify-center rounded-full transition-opacity duration-300',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
              isDark
                ? 'bg-cloud text-charcoal shadow-[0_2px_10px_rgba(0,0,0,0.35)] focus-visible:outline-cloud'
                : 'bg-charcoal text-cloud shadow-[0_2px_10px_rgba(0,0,0,0.18)] focus-visible:outline-charcoal',
              showScrollHint ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <ArrowRight className="size-10" />
          </button>
        </div>

        {hasFooterButtons ? (
          <div className="@container flex w-full justify-center">
            <ButtonRow buttons={props.footerButtons!} theme={buttonTheme} />
          </div>
        ) : null}
      </div>
    </div>
  );
};
